/*
  Carnevore Mountain product page (sections/cm-main-product.liquid and the cm-product-* sections).

  - Add to cart: the form's submit, from the main button or the phone sticky bar (which submits the same form), goes
    to the cart drawer through window.cmCart.add (assets/cm-quick-add.js), the queue the shop pages' quick add uses:
    one add at a time, inside the drawer's busy state, checkout lock and stale-response guard. The drawer then opens,
    and focus returns to the button on close. While an add runs, both buttons are aria-disabled (focus stays on
    them), show a spinner and read "Adding...", and further taps are ignored. A refused add shows Shopify's message
    under the button (role="alert"); Dawn clears it when the size changes. Without the drawer (cart type "Page"),
    Dawn's product form handles the submit as usual.
  - Size changes stay Dawn's (assets/product-info.js); this script follows them (PUB_SUB_EVENTS.variantChange) to
    announce "5 lbs selected, $49.99 CAD." and to update the sticky bar.
  - Sticky add to cart (phones): shows once the main button has scrolled out of view above, and hides again when it
    is back.
  - Order cutoff: fills in the next cutoff date and the time left, every minute. The day and time zone come from the
    order deadline bar (<cm-order-countdown>) when it is on the page, else from the delivery block.
  - Tabs and questions slide open and closed. Motion is off with reduced motion.
*/
(() => {
  if (window.cmProduct) return;
  window.cmProduct = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 749px)');
  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  /* ---------- Buy box ---------- */
  function initProduct(info) {
    if (info.dataset.cmReady) return;
    info.dataset.cmReady = 'true';
    const sectionId = info.dataset.section;
    const formId = `product-form-${sectionId}`;
    let variants = [];
    try {
      variants = JSON.parse(info.querySelector('[data-cm-variants]')?.textContent || '[]');
    } catch (error) {
      variants = [];
    }
    const title = (info.querySelector('.cm-pdp__title')?.textContent || '').trim();
    const announcer = info.querySelector('[data-cm-announce]');
    const sticky = info.querySelector('[data-cm-sticky]');
    const stickyButton = info.querySelector('[data-cm-sticky-add]');
    const mainButton = () => document.getElementById(`ProductSubmitButton-${sectionId}`);
    let busy = false;

    const say = (text) => {
      if (!announcer || !text) return;
      announcer.textContent = '';
      window.setTimeout(() => {
        announcer.textContent = text;
      }, 80);
    };

    // One busy state for both buttons.
    const labelOf = (button) =>
      button && (button.querySelector('[data-cm-sticky-label]') || button.querySelector(':scope > span'));
    const setBusy = (on) => {
      busy = on;
      [mainButton(), stickyButton].forEach((button) => {
        if (!button) return;
        const label = labelOf(button);
        button.classList.toggle('loading', on);
        button.classList.toggle('is-loading', on);
        if (on) {
          button.setAttribute('aria-disabled', 'true');
          if (label) {
            label.dataset.cmLabel = label.textContent;
            label.textContent = info.dataset.adding || label.textContent;
          }
        } else {
          button.removeAttribute('aria-disabled');
          if (label && label.dataset.cmLabel) label.textContent = label.dataset.cmLabel;
        }
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
      });
    };

    info.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== formId) return; // getAttribute: form.id is the form's "id" input
        if (!window.cmCart || !window.cmCart.drawer()) return; // cart type "Page": Dawn's product form posts it
        event.preventDefault();
        event.stopPropagation(); // Dawn's product form handler doesn't run: the drawer's queue does the add
        const main = mainButton();
        if (busy || !main || main.disabled) return;
        const data = new FormData(form);
        const id = Number(data.get('id'));
        if (!id) return;
        const item = { id, quantity: Number(data.get('quantity')) || 1 };
        if (data.get('selling_plan')) item.selling_plan = Number(data.get('selling_plan'));
        const properties = {};
        for (const [key, value] of data.entries()) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match) properties[match[1]] = value;
        }
        if (Object.keys(properties).length) item.properties = properties;

        const button = event.submitter && event.submitter.form === form ? event.submitter : main;
        const productForm = info.querySelector('product-form');
        productForm?.handleErrorMessage?.();
        setBusy(true);
        window.cmCart
          .add([item], { message: fill(info.dataset.added, { title }) })
          .then(() => window.cmCart.drawer()?.open(button.isConnected ? button : main))
          .catch((error) => {
            const message = error.message || (window.cartStrings && window.cartStrings.error) || '';
            if (message) productForm?.handleErrorMessage?.(message);
          })
          .finally(() => setBusy(false));
      },
      true
    );

    // Size changes: announce them and keep the sticky bar in step.
    const stickyMeta = info.querySelector('[data-cm-sticky-meta]');
    const unsubscribe =
      typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
        ? subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || data.sectionId !== sectionId || !data.variant) return;
            const v = variants.find((x) => x.id === data.variant.id);
            if (!v) return;
            const size = v.default ? title : v.title;
            say(fill(v.available ? info.dataset.announceSelected : info.dataset.announceSoldOut, { size, price: v.price }));
            if (stickyMeta) stickyMeta.textContent = v.default ? v.price : `${v.title} · ${v.price}`;
          })
        : null;

    // The sticky button mirrors the main one (Dawn disables it and changes its label for a sold-out size).
    if (stickyButton) {
      const sync = () => {
        const main = mainButton();
        if (!main || busy) return;
        stickyButton.disabled = main.disabled;
        const label = labelOf(stickyButton);
        const mainLabel = labelOf(main);
        if (label && mainLabel) label.textContent = mainLabel.textContent.trim();
      };
      const observer = new MutationObserver(sync);
      const watch = () => {
        const main = mainButton();
        if (main) observer.observe(main, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
      };
      watch();
      sync();
      info.querySelector('[data-cm-sticky-size]')?.addEventListener('click', () => {
        const picker = document.getElementById(`variant-selects-${sectionId}`);
        if (!picker) return;
        picker.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
        (picker.querySelector('input:checked:not(:disabled)') || picker.querySelector('input:not(:disabled)'))?.focus({ preventScroll: true });
      });
    }

    // Sticky bar on phones: shown once the main button has scrolled away above the screen.
    const buy = info.querySelector('[data-cm-buy]');
    if (sticky && buy && 'IntersectionObserver' in window) {
      let past = false;
      const update = () => {
        const show = past && phone.matches;
        if (show === !sticky.hidden) return;
        sticky.hidden = !show;
        document.body.classList.toggle('cm-has-sticky', show);
      };
      new IntersectionObserver(
        ([entry]) => {
          past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          update();
        },
        { threshold: 0 }
      ).observe(buy);
      phone.addEventListener('change', update);
    }

    info.cmCleanup = () => unsubscribe?.();
  }

  /* ---------- Order cutoff ---------- */
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const DAY_MS = 86400000;
  const formatters = new Map();
  const formatterFor = (timeZone) => {
    if (!formatters.has(timeZone)) {
      let formatter = null;
      try {
        formatter = new Intl.DateTimeFormat('en-US', {
          timeZone,
          hourCycle: 'h23',
          weekday: 'short',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        });
      } catch (error) {
        formatter = null;
      }
      formatters.set(timeZone, formatter);
    }
    return formatters.get(timeZone);
  };
  // The store's wall-clock time at an instant, as UTC fields (the same calculation as assets/cm-order-bar.js).
  const wallClock = (instant, timeZone) => {
    const formatter = formatterFor(timeZone);
    const date = new Date(instant);
    if (!formatter) {
      return {
        wall: Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()),
        weekday: date.getDay(),
      };
    }
    const parts = {};
    formatter.formatToParts(date).forEach((part) => {
      parts[part.type] = part.value;
    });
    return {
      wall: Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour) % 24, Number(parts.minute), Number(parts.second)),
      weekday: WEEKDAYS.indexOf(parts.weekday),
    };
  };
  // The next cutoff: midnight at the end of the cutoff day, in the store's time zone.
  const nextCutoff = (cutoffDay, timeZone) => {
    const now = Date.now();
    const current = wallClock(now, timeZone);
    const startOfToday = current.wall - (current.wall % DAY_MS);
    const daysAhead = (cutoffDay - current.weekday + 7) % 7;
    const cutoffDate = new Date(startOfToday + daysAhead * DAY_MS);
    const endWall = startOfToday + (daysAhead + 1) * DAY_MS;
    const offsetNow = current.wall - Math.floor(now / 1000) * 1000;
    const guess = endWall - offsetNow;
    const offsetThen = wallClock(guess, timeZone).wall - guess;
    return { remaining: Math.max(0, endWall - offsetThen - now), cutoffDate };
  };
  const dateLabel = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  const timeLeft = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    return days > 0 ? `${days}d ${hours}h` : `${hours}h ${minutes % 60}m`;
  };

  function initCutoff(line) {
    if (line.dataset.cmReady) return;
    line.dataset.cmReady = 'true';
    const bar = document.querySelector('cm-order-countdown');
    const cutoffDay = Number((bar && bar.dataset.cutoffDay) ?? line.dataset.cutoffDay ?? 0);
    const timeZone = (bar && bar.dataset.timeZone) || line.dataset.timeZone || 'America/Toronto';
    const date = line.querySelector('[data-cm-cutoff-date]');
    const left = line.querySelector('[data-cm-cutoff-left]');
    const wrap = line.querySelector('[data-cm-cutoff-wrap]');
    const tick = () => {
      if (!line.isConnected) return;
      const { remaining, cutoffDate } = nextCutoff(cutoffDay, timeZone);
      if (date) date.textContent = dateLabel.format(cutoffDate);
      if (left) left.textContent = timeLeft(remaining);
      if (wrap) wrap.hidden = false;
      window.setTimeout(tick, 60000 - (Date.now() % 60000) + 50);
    };
    tick();
  }

  /* ---------- Tabs and questions slide open and closed ---------- */
  function initAccordion(details) {
    if (details.dataset.cmReady) return;
    details.dataset.cmReady = 'true';
    const summary = details.querySelector('summary');
    const answer = details.querySelector('[data-cm-answer]');
    if (!summary || !answer || !answer.animate) return;
    let running = false;
    summary.addEventListener('click', (event) => {
      if (reducedMotion.matches) return;
      event.preventDefault();
      if (running) return;
      running = true;
      const opening = !details.open;
      if (opening) details.open = true;
      const height = answer.scrollHeight;
      answer.style.overflow = 'hidden';
      const frames = [{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }];
      const animation = answer.animate(opening ? frames : frames.reverse(), { duration: 220, easing: 'ease' });
      animation.onfinish = animation.oncancel = () => {
        if (!opening) details.open = false;
        answer.style.overflow = '';
        running = false;
      };
    });
  }

  /* ---------- Related products ---------- */
  // Search & Discovery: complementary, then related, for this product, rendered as this section's cards. Replaces the
  // fallback collection's cards when it has any, and tops up from them when it has fewer than the limit.
  class CmProductRelated extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const intents = (this.dataset.intents || '').split(',').filter(Boolean);
      if (!intents.length || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.load(intents);
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }

    load(intents) {
      const limit = Number(this.dataset.limit) || 4;
      const url = (intent) =>
        `${this.dataset.url}?product_id=${encodeURIComponent(this.dataset.productId)}&limit=${limit + 1}` +
        `&intent=${intent}&section_id=${encodeURIComponent(this.dataset.sectionId)}`;
      const get = (intent) =>
        fetch(url(intent))
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => '');
      return Promise.all(intents.map(get)).then((texts) => {
        const seen = new Set([String(this.dataset.productId)]);
        const found = [];
        texts.forEach((text) => {
          if (!text) return;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          doc.querySelectorAll('[data-cm-related-cards] > li').forEach((item) => {
            if (seen.has(item.dataset.productId)) return;
            seen.add(item.dataset.productId);
            found.push(item);
          });
        });
        if (!found.length) return;
        const soldOut = (item) => !!item.querySelector('.is-sold-out');
        const picked = [...found.filter((item) => !soldOut(item)), ...found.filter(soldOut)].slice(0, limit);
        const list = this.querySelector('[data-cm-related-list]');
        if (!list || list.contains(document.activeElement)) return; // never pull the cards away from a shopper using them
        const topUp = [...list.children].filter((item) => !seen.has(item.dataset.productId));
        const items = [...picked.map((item) => document.importNode(item, true)), ...topUp].slice(0, limit);
        list.replaceChildren(...items);
      });
    }
  }
  if (!customElements.get('cm-product-related')) customElements.define('cm-product-related', CmProductRelated);

  function init(root) {
    root.querySelectorAll('product-info.cm-pdp').forEach(initProduct);
    root.querySelectorAll('[data-cm-cutoff]').forEach(initCutoff);
    root.querySelectorAll('.cm-tab, [data-cm-accordion] details').forEach(initAccordion);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('product-info.cm-pdp').forEach((info) => info.cmCleanup?.());
  });
})();
